// Auth API — the JWT lives in an httpOnly cookie (plan 22). Every call
// here assumes `safeFetch` attaches the cookie via `credentials: 'include'`
// and the CSRF header on mutations.

import { ApiError, safeFetch } from '../../shared/api/client';
import { API } from '../../shared/api/endpoints';
import type { UserData } from '../../shared/types';

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterPayload {
  email: string;
  password: string;
  name: string;
  telegram?: string;
  birth_date?: string;
  birth_time?: string;
  birth_time_type?: 'confirmed' | 'approximate' | 'unknown' | string;
  birth_location?: string;
  verification_code: string;
  /**
   * Plan 74 — registration-time marketing-email consent. Optional; absent /
   * false leaves the user opted out (the AuthModal checkbox defaults
   * unchecked). True persists `marketing_consent_source = 'registration'`.
   */
  marketing_consent?: boolean;
}

export async function fetchMe(): Promise<UserData | null> {
  try {
    const u = (await safeFetch(API.me)) as UserData | null;
    return u ?? null;
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return null;
    throw err;
  }
}

export async function login(credentials: LoginCredentials): Promise<UserData> {
  const data = await safeFetch(API.authLogin, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(credentials),
  });
  const u = (data?.user ?? null) as UserData | null;
  if (!u) throw new ApiError('Malformed login response', 500, data);
  return u;
}

export async function registerUser(payload: RegisterPayload): Promise<UserData> {
  const data = await safeFetch(API.authRegister, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const u = (data?.user ?? null) as UserData | null;
  if (!u) throw new ApiError('Malformed register response', 500, data);
  return u;
}

export async function logout(): Promise<void> {
  await safeFetch('/api/auth/logout', { method: 'POST' });
}

export async function sendVerification(email: string): Promise<unknown> {
  return safeFetch(API.authSendVerification, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
}

/**
 * Plan 75 — request a password reset link. Server always returns 200,
 * regardless of outcome; since the owner decision of 2026-07-10 the body
 * carries `accountFound` so the UI can guide users whose email has a typo
 * or was never registered (instead of leaving them waiting for an email
 * that will never arrive).
 */
export async function requestPasswordReset(
  email: string
): Promise<{ success: boolean; accountFound: boolean; message: string }> {
  return (await safeFetch(API.authPasswordResetRequest, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  })) as { success: boolean; accountFound: boolean; message: string };
}

/**
 * Plan 75 — confirm a password reset using the raw token from the email
 * link. Returns 400 if the token is invalid, expired, or already used.
 */
export async function confirmPasswordReset(
  token: string,
  newPassword: string
): Promise<{ success: boolean }> {
  return (await safeFetch(API.authPasswordResetConfirm, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, new_password: newPassword }),
  })) as { success: boolean };
}
